import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable, getSelectColumn, SortableHeader } from '@/components/ui/data-table';
import { StatusBadge } from '@/components/ui/status-badge';
import { FloatingActionBar } from '@/components/ui/floating-action-bar';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { purchaseOrderApi, queryFn, type PurchaseOrder } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { MoreVertical, FileText, Send, Archive, Trash } from 'lucide-react';

const PAGE_SIZE = 25;

function PurchaseOrdersContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness, isViewOnly } = useApp();
  const queryClient = useQueryClient();
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<PurchaseOrder[]>([]);

  // Server-side pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const getOrderingParam = () => {
    if (sorting.length === 0) return undefined;
    const { id, desc } = sorting[0];
    return desc ? `-${id}` : id;
  };

  const { data: poPage, isLoading } = useQuery({
    queryKey: ['purchase-orders', currentBusiness?.id, pageIndex, sorting],
    queryFn: () => queryFn(purchaseOrderApi.list(
      currentBusiness!.id,
      undefined,
      undefined,
      undefined,
      undefined,
      {
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        ordering: getOrderingParam(),
      }
    )),
    enabled: !!currentBusiness,
  });

  const purchaseOrders = poPage?.results ?? [];
  const totalCount = poPage?.count ?? 0;

  const invalidatePOs = () => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });

  const handleDeletePurchaseOrder = async (id: string) => {
    if (!confirm('Are you sure you want to delete this purchase order?')) return;
    const response = await purchaseOrderApi.delete(id);
    if (response.error) {
      alert(response.error);
    } else {
      invalidatePOs();
    }
  };

  // Bulk action handlers
  const handleBulkArchive = async () => {
    for (const po of selectedPurchaseOrders) {
      await purchaseOrderApi.archive(po.id);
    }
    setSelectedPurchaseOrders([]);
    invalidatePOs();
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedPurchaseOrders.length} purchase order(s)?`)) return;
    for (const po of selectedPurchaseOrders) {
      await purchaseOrderApi.delete(po.id);
    }
    setSelectedPurchaseOrders([]);
    invalidatePOs();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };


  const columns: ColumnDef<PurchaseOrder>[] = [
    getSelectColumn<PurchaseOrder>(),
    {
      accessorKey: 'purchase_order_number',
      header: ({ column }) => <SortableHeader column={column} title="PO #" />,
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.getValue('purchase_order_number')}</span>
      ),
    },
    {
      accessorKey: 'vendor_name',
      header: ({ column }) => <SortableHeader column={column} title="Vendor" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.vendor_name || '-'}</div>
          {row.original.vendor_company && (
            <div className="text-xs text-muted-foreground truncate">{row.original.vendor_company}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'issued_date',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDate(row.getValue('issued_date'))}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;

        const config: Record<string, { dot: 'gray' | 'blue' | 'green'; label: string }> = {
          draft: { dot: 'gray', label: 'Draft' },
          sent: { dot: 'blue', label: 'Sent' },
          accepted: { dot: 'green', label: 'Accepted' },
        };

        const statusConfig = config[status] || { dot: 'gray', label: status };
        return <StatusBadge dot={statusConfig.dot} label={statusConfig.label} />;
      },
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Total" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-sm font-medium">
          {formatAmount(row.getValue('total_amount'))}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePurchaseOrder(row.original.id);
                }}
                className="text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage>
        <ListPageHeader>
          <ListPageTitle>Purchase Orders</ListPageTitle>
          <ListPageActions>
            {!isViewOnly && (
              <Button onClick={() => nav('/purchase-orders/create')} className="rounded-full">
                <Send className="h-4 w-4" />
                New Purchase Order
              </Button>
            )}
          </ListPageActions>
        </ListPageHeader>

        <ListPageCard>
          <ListPageContent>
            <DataTable
              columns={columns}
              data={purchaseOrders}
              loading={isLoading}
              onRowClick={(po) => nav(`/purchase-orders/${po.id}`)}
              noBorder
              enableRowSelection
              onSelectionChange={setSelectedPurchaseOrders}
              emptyIcon={<FileText className="h-6 w-6 text-muted-foreground" />}
              emptyTitle="No purchase orders yet"
              emptyDescription="Create your first purchase order to get started."
              // Server-side pagination
              manualPagination
              manualSorting
              pageSize={PAGE_SIZE}
              pageIndex={pageIndex}
              totalCount={totalCount}
              onPaginationChange={(newPageIndex) => setPageIndex(newPageIndex)}
              sorting={sorting}
              onSortingChange={(newSorting) => {
                setSorting(newSorting);
                setPageIndex(0); // Reset to first page when sort changes
              }}
            />
          </ListPageContent>
        </ListPageCard>
      </ListPage>

      <FloatingActionBar
        selectedCount={selectedPurchaseOrders.length}
        onClear={() => setSelectedPurchaseOrders([])}
      >
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkArchive}
        >
          <Archive className="h-4 w-4 mr-1" /> Archive
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkDelete}
        >
          <Trash className="h-4 w-4 mr-1" /> Delete
        </Button>
      </FloatingActionBar>
    </>
  );
}

export function PurchaseOrders() {
  return <PurchaseOrdersContent />;
}
